/**
 * Canonical Phase-1 enum value arrays.
 * Zod schemas and Drizzle PG enums MUST derive from these — no duplicate literals.
 * Kept free of Zod/Drizzle imports to avoid runtime cycles.
 */

export const USER_ROLES = ["member", "approver", "admin"] as const;

export const RESEARCH_JOB_STATUSES = [
  "queued",
  "running",
  "cancelling",
  "cancelled",
  "completed",
  "failed",
] as const;

export const RESEARCH_STAGES = [
  "discovery",
  "normalization",
  "deduplication",
  "website_resolution",
  "crawl",
  "extraction",
  "contact_research",
  "validation",
  "scoring",
  "draft_preparation",
  "final_persistence",
  "done",
] as const;

/**
 * Valid non-null values for research_candidates.last_completed_stage.
 * Job-level stages (discovery/normalization/deduplication/done) are NOT allowed.
 * Resume continues at the successor of this stage.
 */
export const CANDIDATE_RESUME_STAGES = [
  "website_resolution",
  "crawl",
  "extraction",
  "contact_research",
  "validation",
  "scoring",
  "draft_preparation",
  "final_persistence",
] as const;

/** @deprecated Alias — use CANDIDATE_RESUME_STAGES */
export const CANDIDATE_PIPELINE_STAGES = CANDIDATE_RESUME_STAGES;

export const CANDIDATE_STATUSES = [
  "discovered",
  "normalized",
  "duplicate",
  "website_resolved",
  "crawled",
  "extracted",
  "contacts_found",
  "validated",
  "scored",
  "drafted",
  "persisted",
  "skipped",
  "failed",
] as const;

export const PIPELINE_STATUSES = [
  "discovered",
  "researched",
  "qualified",
  "ready_for_outreach",
  "paused",
  "disqualified",
] as const;

export const EMAIL_STATUSES = [
  "published",
  "verified",
  "inferred",
  "unverified",
  "invalid",
] as const;

/** Email statuses that must not carry verified_at (Zod + PG check). */
export const EMAIL_STATUSES_FORBIDDING_VERIFIED_AT = [
  "inferred",
  "invalid",
] as const satisfies ReadonlyArray<(typeof EMAIL_STATUSES)[number]>;

export const DRAFT_STATUSES = [
  "draft",
  "pending_approval",
  "approved",
  "rejected",
  "cancelled",
] as const;

/** Draft statuses excluded from the active-draft uniqueness index. */
export const INACTIVE_DRAFT_STATUSES = [
  "rejected",
  "cancelled",
] as const satisfies ReadonlyArray<(typeof DRAFT_STATUSES)[number]>;

export const DRAFT_PURPOSES = ["initial", "followup", "reply"] as const;

export const AGENT_RUN_KINDS = ["chat", "research_stage", "tool_preview"] as const;

export const AGENT_RUN_STATUSES = [
  "running",
  "completed",
  "failed",
  "cancelled",
] as const;

export const COST_KINDS = [
  "web_search",
  "llm_tokens",
  "browser_ms",
  "other",
] as const;

export const ARTIFACT_KINDS = [
  "crawl_html",
  "crawl_markdown",
  "screenshot",
  "pdf",
  "other",
] as const;

export const MESSAGE_ROLES = ["user", "assistant", "tool", "system"] as const;

export const TASK_STATUSES = ["open", "done", "cancelled"] as const;

export const MODEL_ROLES = [
  "MODEL_CHAT",
  "MODEL_RESEARCH",
  "MODEL_EXTRACTION",
  "MODEL_OUTREACH",
  "MODEL_CLASSIFICATION",
] as const;

export const ERROR_CODES = [
  "VALIDATION_ERROR",
  "UNAUTHORIZED",
  "NOT_FOUND",
  "FORBIDDEN",
  "BUDGET_EXCEEDED",
  "KILL_SWITCH",
  "RATE_LIMIT",
  "PROVIDER_ERROR",
  "SSRF_BLOCKED",
  "CONFLICT",
  "NOT_IMPLEMENTED",
] as const;

/** Narrow readonly tuple for Drizzle/Zod enum constructors */
export function asNonEmptyEnumTuple<T extends string>(
  values: readonly T[],
): [T, ...T[]] {
  if (values.length === 0) {
    throw new Error("enum values must be non-empty");
  }
  return values as [T, ...T[]];
}
